/**
 * Approving a `ploy` command line sign-in.
 *
 * The CLI signs a member in with the OAuth device grant: the terminal prints a
 * short code, the member types it into the portal, and approving it is what
 * turns the attempt into a credential on that machine. Three properties of the
 * server contract shape everything in this file.
 *
 *   1. The code never goes in a URL. All three calls are POSTs carrying the
 *      code in the body, which is how the server mounts them, so a code cannot
 *      leak through access logs, browser history or a `Referer` header.
 *   2. Nothing here retries or polls. The routes carry an IP rate limit of ten
 *      a minute, and a failed lookup is a dead end until the member retypes.
 *      That is also why these are mutations rather than queries: a `useQuery`
 *      would refetch on window focus and spend the budget on its own.
 *   3. The responses are plain JSON, not the `{ success, data }` envelope the
 *      rest of the members API uses, so `membersApi.post` cannot be used here
 *      (it rejects anything without `success: true`). The fetch is spelled out
 *      instead, still routed through `readBody` so an IP block is still caught
 *      the same way it is everywhere else.
 */
import { useMutation } from "@tanstack/react-query";
import { API_BASE, getSubdomain, refresh } from "modules/members/api";
import { readBody } from "modules/members/api/ipRestriction";
import { useMemberAuthStore } from "modules/members/auth";
import { memberErrorMessage } from "./memberErrors";
import { NETWORK_MESSAGE, SESSION_MESSAGE } from "./stepUpFailure";

// The same words as the passkey calls use; defined once, in stepUpFailure.
export { NETWORK_MESSAGE, SESSION_MESSAGE };

/** A pending sign-in attempt, as described by POST /members/cli/device/lookup. */
export type CliDeviceAttempt = {
  userCode: string;
  expiresAt: string;
  requestedAt: string;
  requestedIp?: string | null;
  requestedUserAgent?: string | null;
};

/** A failed call, normalised to one message the page can render. */
export type CliDeviceFailure = {
  /** HTTP status, or null when the request never reached the server. */
  status: number | null;
  message: string;
};

// Codes are two groups of four over an alphabet with no 0/1/I/L/O, so the
// shape below mirrors the server's own formatting. Validating the length
// locally keeps an obvious typo from spending one of ten attempts a minute and
// coming back as "that code is not valid", which reads like the code is wrong
// rather than incomplete.
const GROUP_SIZE = 4;
export const USER_CODE_LENGTH = 8;

export const RATE_LIMITED_MESSAGE =
  "Too many tries. Wait a minute, then try again.";

const LOOKUP_FALLBACK =
  "That code is not valid. Check your terminal and try again.";
const APPROVE_FALLBACK =
  "That code could not be approved. It may have expired or already been used.";
const DENY_FALLBACK =
  "That code could not be denied. It may have expired or already been used.";

/** Everything a person might type, reduced to the characters that matter. */
export const stripUserCode = (raw: string): string =>
  (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * Regroups a typed code into the shape the terminal printed it in.
 *
 * Matches the server's own normalisation, so lower case, missing hyphens and
 * stray spaces all resolve to the same code. Rejecting a correct code over its
 * punctuation teaches people to paste, and pasting is the habit this flow
 * exists to avoid.
 */
export const formatUserCode = (raw: string): string => {
  const stripped = stripUserCode(raw);
  const groups: string[] = [];
  for (let i = 0; i < stripped.length; i += GROUP_SIZE) {
    groups.push(stripped.slice(i, i + GROUP_SIZE));
  }
  return groups.join("-");
};

export const isCompleteUserCode = (raw: string): boolean =>
  stripUserCode(raw).length === USER_CODE_LENGTH;

/**
 * One message per failure, chosen by status first.
 *
 * A rate limit and an expired session both come back without a usable body, so
 * neither can be described from the payload; everything else prefers the
 * server's own wording, which is already written for a person.
 */
export const cliDeviceFailure = (
  status: number | null,
  body: unknown,
  fallback: string,
): CliDeviceFailure => {
  if (status === 429) return { status, message: RATE_LIMITED_MESSAGE };
  if (status === 401) return { status, message: SESSION_MESSAGE };
  return { status, message: memberErrorMessage(body, fallback) };
};

// The portal refreshes its access token on mount, so by the time a code has
// been typed there is normally one in the store. Fetching it here rather than
// reading it at render time means a member who left the page open long enough
// for the store to be empty still gets one call, not a failure.
const accessToken = async (): Promise<string> => {
  const current = useMemberAuthStore.getState().accessToken;
  if (current) return current;

  const refreshed = await refresh().catch(() => null);
  if (refreshed?.accessToken) return refreshed.accessToken;

  throw { status: 401, message: SESSION_MESSAGE } as CliDeviceFailure;
};

const post = async <T>(
  path: string,
  userCode: string,
  fallback: string,
): Promise<T> => {
  const token = await accessToken();

  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
      body: JSON.stringify({ userCode }),
    });
  } catch {
    throw { status: null, message: NETWORK_MESSAGE } as CliDeviceFailure;
  }

  // A rate-limited response is not JSON, so parsing has to be allowed to fail
  // without turning into "something went wrong".
  const body = await readBody<unknown>(res).catch(() => null);
  if (!res.ok) throw cliDeviceFailure(res.status, body, fallback);
  return body as T;
};

export const lookupCliDevice = (userCode: string): Promise<CliDeviceAttempt> =>
  post<CliDeviceAttempt>(
    "members/cli/device/lookup",
    formatUserCode(userCode),
    LOOKUP_FALLBACK,
  );

export const approveCliDevice = (
  userCode: string,
): Promise<{ approved: boolean }> =>
  post<{ approved: boolean }>(
    "members/cli/device/approve",
    formatUserCode(userCode),
    APPROVE_FALLBACK,
  );

export const denyCliDevice = (userCode: string): Promise<{ denied: boolean }> =>
  post<{ denied: boolean }>(
    "members/cli/device/deny",
    formatUserCode(userCode),
    DENY_FALLBACK,
  );

// `retry: false` is the default for mutations, and it is pinned here anyway:
// silently repeating a call against a ten-a-minute limit would burn a member's
// remaining tries on a failure they can already see.
const NO_RETRY = { retry: false as const };

export const useCliDeviceLookup = () =>
  useMutation<CliDeviceAttempt, CliDeviceFailure, string>(
    (userCode) => lookupCliDevice(userCode),
    NO_RETRY,
  );

export const useCliDeviceApprove = () =>
  useMutation<{ approved: boolean }, CliDeviceFailure, string>(
    (userCode) => approveCliDevice(userCode),
    NO_RETRY,
  );

export const useCliDeviceDeny = () =>
  useMutation<{ denied: boolean }, CliDeviceFailure, string>(
    (userCode) => denyCliDevice(userCode),
    NO_RETRY,
  );
